import React, { useState, useEffect, useContext } from 'react';
import leagueService from '../services/leagueService';
import tournamentService from '../services/tournamentService';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from './AuthContext';
import '../styles/CreateLeague.css';
import { trackEvent } from '../utils/analytics';

const CreateLeague = () => {
	const [step, setStep] = useState(1);
	const [name, setName] = useState('');
	const [sportType, setSportType] = useState('FOOTBALL');
	const [tournaments, setTournaments] = useState([]);
	const [selectedTournament, setSelectedTournament] = useState('');
	const [scoringType, setScoringType] = useState('CASUAL');
	const [visibilityType, setVisibilityType] = useState('PRIVATE');
	const [error, setError] = useState('');
	const navigate = useNavigate();
	const { role } = useContext(AuthContext);
	const { t } = useTranslation();

	useEffect(() => {
		trackEvent('League Creation Started');
		const fetchData = async () => {
			try {
				const tournamentsData = await tournamentService.getTournamentsWithUpcomingMatches(sportType);
				setTournaments(tournamentsData);
			} catch (error) {
				setError('Failed to load data');
			}
		};

		if (sportType) {
			fetchData();
		}
	}, [sportType]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			const leagueRequest = {
				name,
				sportType,
				scoringType,
				tournamentId: selectedTournament,
				visibilityType
			};

			await leagueService.createLeague(leagueRequest);

			trackEvent('League Created', {
				sport: sportType,
				visibility: visibilityType,
				tournament: selectedTournament,
				scoring_type: scoringType
			});
			navigate(`/dashboard`);
		} catch (error) {
			trackEvent('League Creation Failed', {
				sport: sportType,
				step: 'submission'
			});
			setError('Failed to create league');
		}
	};

	const nextStep = () => {
		trackEvent('League Creation Step', { 
			step: step,
			next_step: step + 1 
		});
		setStep((prev) => prev + 1);
	};
	const prevStep = () => setStep((prev) => prev - 1);

	const handleKeyDown = (e) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			if (step === 1 && name) nextStep();
			else if (step === 2 && sportType) nextStep();
			else if (step === 3 && selectedTournament) handleSubmit(e);
		}
	};

	return (
		<div className="create-league-container">
			{error && <div className="error-message">{error}</div>}

			<div className="create-league-form">
				<form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
					{/* Krok 1: Zadejte název ligy */}
					{step === 1 && (
						<div className="create-league-step">
							<label>{t('createLeagueSteps.leagueName')}: </label>
							<input
								type="text"
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder={t('createLeagueSteps.leagueNamePlaceholder', 'Zadejte název ligy')}
								required
							/>
							<div className="button-container only-next">
								<button type="button" className="btn-primary" onClick={nextStep} disabled={!name}>
									{t('createLeagueSteps.next')}
								</button>
							</div>
						</div>
					)}

					{/* Krok 2: Výběr sportu */}
					{step === 2 && (
						<div className="create-league-step">
							<label>{t('createLeagueSteps.selectSportType')}: </label>
							<select value={sportType} onChange={(e) => setSportType(e.target.value)} required>
								<option value="FOOTBALL">{t('football')}</option>
								<option value="HOCKEY">{t('iceHockey')}</option>
							</select>
							<div className="button-container">
								<button type="button" className="btn-secondary" onClick={prevStep}>
                                    {t('createLeagueSteps.back')}
                                </button>
								<button type="button" className="btn-primary" onClick={nextStep} disabled={!sportType}>
                                    {t('createLeagueSteps.next')}
                                </button>
							</div>
						</div>
					)}

					{/* Krok 3: Výběr soutěže */}
					{step === 3 && (
						<div className="create-league-step">
							<label>{t('tournamentLabel')}: </label>
							<select value={selectedTournament} onChange={(e) => setSelectedTournament(e.target.value)} required>
								<option value="">{t('createLeagueSteps.selectTournament')}</option>
								{tournaments.map((tournament) => (
									<option key={tournament.id} value={tournament.id}>
										{t(`tournaments.${tournament.name}`, { year: tournament.year })}
									</option>
								))}
							</select>
							<div style={{ marginTop: '15px' }}>
								<label>{t('createLeagueSteps.scoringType', { defaultValue: 'Způsob bodování' })}: </label>
								<select value={scoringType} onChange={(e) => setScoringType(e.target.value)} required>
									<option value="CASUAL">{t('createLeagueSteps.scoringCasual', { defaultValue: 'Casual (pevné body 10/7/4/2)' })}</option>
									<option value="COMPETITIVE">{t('createLeagueSteps.scoringCompetitive', { defaultValue: 'Competitive (body podle pozice favorit/outsider)' })}</option>
								</select>
							</div>

							{(role === 'ADMIN' || role === 'SUPER_ADMIN') && (
								<div style={{ marginTop: '15px' }}>
									<label>{t('createLeagueSteps.visibility')}: </label>
									<select value={visibilityType} onChange={(e) => setVisibilityType(e.target.value)} required>
										<option value="PRIVATE">{t('createLeagueSteps.private')}</option>
										<option value="PUBLIC">{t('createLeagueSteps.public')}</option>
									</select>
								</div>
							)}
							<div className="button-container">
								<button type="button" className="btn-secondary" onClick={prevStep}>
									{t('createLeagueSteps.back')}
								</button>
								<button type="submit" className="btn-primary" disabled={!selectedTournament}>
									{t('createLeagueSteps.submit')}
								</button>
							</div>
						</div>
					)}
				</form>
			</div>
		</div>
	);
};

export default CreateLeague;